import { useState, useEffect } from 'react'
import { MessageCircle, Send, Reply, CheckCircle, Clock, User, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'

interface Discussion {
  _id: string
  title: string
  content: string
  user: {
    _id: string
    name: string
    avatar?: string
  }
  replies: Array<{
    _id: string
    user: {
      _id: string
      name: string
      avatar?: string
      role?: string
    }
    content: string
    createdAt: string
    isAdminReply: boolean
  }>
  isResolved: boolean
  resolvedBy?: {
    name: string
  }
  resolvedAt?: string
  createdAt: string
  lastActivity: string
  videoTimestamp: number
}

interface DiscussionPanelProps {
  courseId: string
  lessonId: string
  lessonTitle?: string
  videoTimestamp?: number
  isVisible?: boolean
  onClose?: () => void
  inline?: boolean // New prop for inline rendering (inside a tab)
}

export default function DiscussionPanel({
  courseId,
  lessonId,
  lessonTitle = 'Lesson Discussion',
  videoTimestamp = 0,
  isVisible = true,
  onClose,
  inline = false
}: DiscussionPanelProps) {
  const [discussions, setDiscussions] = useState<Discussion[]>([])
  const [loading, setLoading] = useState(false)
  const [newDiscussion, setNewDiscussion] = useState({ title: '', content: '' })
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [expandedDiscussions, setExpandedDiscussions] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // Load discussions when panel becomes visible
  useEffect(() => {
    if (isVisible && courseId && lessonId) {
      loadDiscussions()
    }
  }, [isVisible, courseId, lessonId])

  const loadDiscussions = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${courseId}/${lessonId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load discussions')
      }

      const data = await response.json()
      setDiscussions(data.discussions)
    } catch (err) {
      console.error('Error loading discussions:', err)
      setError(err instanceof Error ? err.message : 'Failed to load discussions')
    } finally {
      setLoading(false)
    }
  }

  const createDiscussion = async () => {
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${courseId}/${lessonId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newDiscussion,
          videoTimestamp
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create discussion')
      }

      const data = await response.json()
      setDiscussions(prev => [data.discussion, ...prev])
      setNewDiscussion({ title: '', content: '' })
      setExpandedDiscussions(prev => new Set([data.discussion._id, ...prev]))
    } catch (err) {
      console.error('Error creating discussion:', err)
      setError(err instanceof Error ? err.message : 'Failed to create discussion')
    }
  }

  const addReply = async (discussionId: string) => {
    if (!replyContent.trim()) return

    try {
      console.log('Adding reply:', { discussionId, content: replyContent })

      const token = localStorage.getItem('token')
      const response = await fetch(`/api/discussions/${discussionId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent.trim() })
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Error response:', error)
        throw new Error(error.error || 'Failed to add reply')
      }

      const data = await response.json()
      console.log('Reply added successfully:', data)
      setDiscussions(prev => prev.map(d =>
        d._id === discussionId ? data.discussion : d
      ))
      setReplyContent('')
      setReplyingTo(null)
    } catch (err) {
      console.error('Error adding reply:', err)
      setError(err instanceof Error ? err.message : 'Failed to add reply')
    }
  }

  const toggleDiscussion = (discussionId: string) => {
    setExpandedDiscussions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(discussionId)) {
        newSet.delete(discussionId)
      } else {
        newSet.add(discussionId)
      }
      return newSet
    })
  }

  const formatTime = (timestamp: number) => {
    const minutes = Math.floor(timestamp / 60)
    const seconds = Math.floor(timestamp % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)
    const diffDays = diffMs / (1000 * 60 * 60 * 24)

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)} hour${Math.floor(diffHours) !== 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${Math.floor(diffDays)} day${Math.floor(diffDays) !== 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  if (!isVisible) return null

  // Inline mode - render without the fixed sidebar wrapper
  if (inline) {
    return (
      <div className="space-y-4">
        {/* New Discussion Form */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Discussion title..."
              value={newDiscussion.title}
              onChange={(e) => setNewDiscussion(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <textarea
              placeholder="Ask a question or start a discussion..."
              value={newDiscussion.content}
              onChange={(e) => setNewDiscussion(prev => ({ ...prev, content: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {videoTimestamp > 0 && `At ${formatTime(videoTimestamp)}`}
              </span>
              <button
                onClick={createDiscussion}
                disabled={!newDiscussion.title.trim() || !newDiscussion.content.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Send className="h-3 w-3" />
                Post
              </button>
            </div>
          </div>
        </div>

        {/* Discussions List */}
        <div className="space-y-4">
          {loading ? (
            <div className="py-8 text-center">
              <div className="w-6 h-6 border-2 border-purple-200 dark:border-purple-800 rounded-full animate-spin border-t-purple-600 dark:border-t-purple-400 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading discussions...</p>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          ) : discussions.length === 0 ? (
            <div className="py-8 text-center">
              <MessageCircle className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">No discussions yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Be the first to ask a question!</p>
            </div>
          ) : (
            discussions.map((discussion) => {
              const isExpanded = expandedDiscussions.has(discussion._id)
              const replyCount = discussion.replies.length

              return (
                <div key={discussion._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  {/* Discussion Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {discussion.title}
                        </h4>
                        {discussion.isResolved && (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <span>{discussion.user.name}</span>
                        <span>•</span>
                        <span>{formatDate(discussion.createdAt)}</span>
                        {discussion.videoTimestamp > 0 && (
                          <>
                            <span>•</span>
                            <span>At {formatTime(discussion.videoTimestamp)}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {discussion.content}
                      </p>
                    </div>
                  </div>

                  {/* Discussion Actions */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => toggleDiscussion(discussion._id)}
                        className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium flex items-center gap-1"
                      >
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                      </button>
                      {discussion.isResolved && discussion.resolvedBy && (
                        <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Resolved by {discussion.resolvedBy.name}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setReplyingTo(discussion._id)}
                      className="text-xs text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 font-medium flex items-center gap-1"
                    >
                      <Reply className="h-3 w-3" />
                      Reply
                    </button>
                  </div>

                  {/* Reply Form */}
                  {replyingTo === discussion._id && (
                    <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <textarea
                        placeholder="Write your reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => {
                            setReplyingTo(null)
                            setReplyContent('')
                          }}
                          className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => addReply(discussion._id)}
                          disabled={!replyContent.trim()}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-xs font-medium transition-colors"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {isExpanded && discussion.replies.length > 0 && (
                    <div className="space-y-3 mt-3 pl-11 border-l-2 border-gray-100 dark:border-gray-700">
                      {discussion.replies.map((reply) => (
                        <div key={reply._id} className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${reply.isAdminReply
                              ? 'bg-green-100 dark:bg-green-900/30'
                              : 'bg-blue-100 dark:bg-blue-900/30'
                            }`}>
                            <User className={`h-3 w-3 ${reply.isAdminReply
                                ? 'text-green-600'
                                : 'text-blue-600'
                              }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${reply.isAdminReply
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-blue-700 dark:text-blue-300'
                                }`}>
                                {reply.user.name}
                                {reply.isAdminReply && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                                    Admin
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(reply.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                              {reply.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-rose-50 to-transparent dark:from-rose-900/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-rose-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Discussions</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
          {lessonTitle}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* New Discussion Form */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Discussion title..."
              value={newDiscussion.title}
              onChange={(e) => setNewDiscussion(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
            <textarea
              placeholder="Ask a question or start a discussion..."
              value={newDiscussion.content}
              onChange={(e) => setNewDiscussion(prev => ({ ...prev, content: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {videoTimestamp > 0 && `At ${formatTime(videoTimestamp)}`}
              </span>
              <button
                onClick={createDiscussion}
                disabled={!newDiscussion.title.trim() || !newDiscussion.content.trim()}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Send className="h-3 w-3" />
                Post
              </button>
            </div>
          </div>
        </div>

        {/* Discussions List */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-rose-200 dark:border-mauve-800 rounded-full animate-spin border-t-rose-600 dark:border-t-rose-400 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading discussions...</p>
            </div>
          ) : error ? (
            <div className="p-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          ) : discussions.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">No discussions yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Be the first to ask a question!</p>
            </div>
          ) : (
            discussions.map((discussion) => {
              const isExpanded = expandedDiscussions.has(discussion._id)
              const replyCount = discussion.replies.length

              return (
                <div key={discussion._id} className="p-4">
                  {/* Discussion Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-rose-100 dark:bg-mauve-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-rose-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {discussion.title}
                        </h4>
                        {discussion.isResolved && (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <span>{discussion.user.name}</span>
                        <span>•</span>
                        <span>{formatDate(discussion.createdAt)}</span>
                        {discussion.videoTimestamp > 0 && (
                          <>
                            <span>•</span>
                            <span>At {formatTime(discussion.videoTimestamp)}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {discussion.content}
                      </p>
                    </div>
                  </div>

                  {/* Discussion Actions */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => toggleDiscussion(discussion._id)}
                        className="text-xs text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 font-medium flex items-center gap-1"
                      >
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                      </button>
                      {discussion.isResolved && discussion.resolvedBy && (
                        <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Resolved by {discussion.resolvedBy.name}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setReplyingTo(discussion._id)}
                      className="text-xs text-gray-600 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400 font-medium flex items-center gap-1"
                    >
                      <Reply className="h-3 w-3" />
                      Reply
                    </button>
                  </div>

                  {/* Reply Form */}
                  {replyingTo === discussion._id && (
                    <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <textarea
                        placeholder="Write your reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => {
                            setReplyingTo(null)
                            setReplyContent('')
                          }}
                          className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => addReply(discussion._id)}
                          disabled={!replyContent.trim()}
                          className="px-3 py-1 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-xs font-medium transition-colors"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {isExpanded && discussion.replies.length > 0 && (
                    <div className="space-y-3 mt-3 pl-11">
                      {discussion.replies.map((reply) => (
                        <div key={reply._id} className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${reply.isAdminReply
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : 'bg-blue-100 dark:bg-blue-900/30'
                            }`}>
                            <User className={`h-3 w-3 ${reply.isAdminReply
                              ? 'text-green-600'
                              : 'text-blue-600'
                              }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium ${reply.isAdminReply
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-blue-700 dark:text-blue-300'
                                }`}>
                                {reply.user.name}
                                {reply.isAdminReply && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                                    Admin
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(reply.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                              {reply.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
